/**
 * Web-side view of the application surface manifest.
 *
 * The list itself — titles, urls, gating permissions, landing-page precedence —
 * lives in `@clockwise/shared` (`navigation.ts`) so the smoke suite can generate
 * a post-release check per section from the SAME list the sidebar renders. This
 * module adds the one thing that can't live there: the icons, which would drag
 * `lucide-react` into a package the API imports.
 *
 * Add a section in `@clockwise/shared`, then add its icon here. The
 * `navigation.test.ts` invariant fails if you forget the icon.
 */
import {
  type NavItem as SharedNavItem,
  type SetupNavItem as SharedSetupNavItem,
  navItems as sharedNavItems,
  setupNav as sharedSetupNav,
} from '@clockwise/shared';
import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  Bell,
  BookOpenText,
  Briefcase,
  Building,
  Building2,
  CalendarDays,
  CheckSquare,
  Clock,
  GitBranch,
  LayoutDashboard,
  Network,
  Package,
  Receipt,
  ReceiptText,
  ScrollText,
  Settings2,
  ShieldCheck,
  Sparkles,
  Users,
  Wallet,
} from 'lucide-react';

export {
  hasNavAccess,
  landingTitleForPath,
  resolveFirstSetupPath,
  resolveLandingPath,
  SETUP_PERMISSIONS,
} from '@clockwise/shared';

export type NavItem = SharedNavItem & { icon: LucideIcon };
export type SetupNavItem = SharedSetupNavItem & { icon: LucideIcon };

/** Icon per primary nav url. Keyed by url so a title rename can't orphan one. */
const NAV_ICONS: Record<string, LucideIcon> = {
  '/timesheets': Clock,
  '/approvals': CheckSquare,
  '/reports': BarChart3,
  '/dashboards': LayoutDashboard,
  '/customers': Building2,
  '/invoices': Wallet,
  '/users': Users,
  '/audit': ScrollText,
};

/** Icon per setup sub-section path. */
const SETUP_ICONS: Record<string, LucideIcon> = {
  '/setup/company': Building,
  '/setup/business-units': Network,
  '/setup/activities': Package,
  '/setup/ratecards': Receipt,
  '/setup/expense-catalogs': ReceiptText,
  '/setup/system-roles': ShieldCheck,
  '/setup/service-roles': Briefcase,
  '/setup/departments': Building2,
  '/setup/skills': Sparkles,
  '/setup/chart-of-accounts': BookOpenText,
  '/setup/holidays': CalendarDays,
  '/setup/custom-fields': Settings2,
  '/setup/approval-policies': GitBranch,
  '/setup/notifications': Bell,
};

/** Fallback so a section added to shared without an icon still renders. */
const FALLBACK_ICON: LucideIcon = Settings2;

/** Primary sidebar navigation, in display order, with icons attached. */
export const navItems: NavItem[] = sharedNavItems.map((item) => ({
  ...item,
  icon: NAV_ICONS[item.url] ?? FALLBACK_ICON,
}));

/** Setup sub-sections, in display order, with icons attached. */
export const setupNav: SetupNavItem[] = sharedSetupNav.map((item) => ({
  ...item,
  icon: SETUP_ICONS[item.to] ?? FALLBACK_ICON,
}));
